import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Network, Users, FolderKanban } from "lucide-react";

/**
 * Iter 42.3 — Team Hierarchy Overview card on the Projects tab.
 *
 * Admin / Super-Admin / Department-Admin (of Content):
 *   • sees every Content Manager
 *   • each CM row expands to a list of their Team Leads
 *   • each TL row expands to the projects assigned to that TL under that CM
 *
 * Content Manager:
 *   • sees ONLY their own tree (self as CM + their TLs + their projects)
 *
 * Other roles: card is hidden (parent decides by omitting the render).
 *
 * The card is collapsible (default collapsed) so it doesn't dominate the
 * page. Counters are always visible in the header for a quick scan.
 */
export default function TeamHierarchyCard({ user, projects, users }) {
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isCM = user?.role === "content_manager";
  const isDA = user?.role === "department_admin" && (user?.departments || []).includes("content");
  const [open, setOpen] = useState(false);
  const [expandedCMs, setExpandedCMs] = useState(() => new Set());
  const [expandedTLs, setExpandedTLs] = useState(() => new Set());

  const toggleCM = (id) => setExpandedCMs((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleTL = (id) => setExpandedTLs((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const userMap = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  // Every user who holds a CM role — top-level `role` OR any positions[]
  // entry with role="content_manager". Multi-role aware so a user who is
  // both a CM and a TL still shows in the CM list.
  const allCMs = useMemo(() => {
    return (users || []).filter((u) => {
      if (u.role === "content_manager") return true;
      return (u.positions || []).some((p) => p?.role === "content_manager");
    });
  }, [users]);

  // Only Content-department projects matter for this card (SEO/Marketing/HR
  // projects have their own hierarchies and typically no CM/TL structure).
  const contentProjects = useMemo(
    () => (projects || []).filter((p) => (p.department || "content") === "content"),
    [projects],
  );

  // For a given CM: their TLs (users whose manager_id === cm.id via
  // top-level OR positions[]) AND the projects where the CM is on
  // content_manager_ids.
  const buildCMBranch = (cm) => {
    const cmProjects = contentProjects.filter((p) => (p.content_manager_ids || []).includes(cm.id));
    const tls = (users || []).filter((u) => {
      if (u.role !== "team_lead" && !(u.positions || []).some((p) => p?.role === "team_lead")) return false;
      if (u.manager_id === cm.id) return true;
      return (u.positions || []).some((p) => p?.manager_id === cm.id);
    });
    return { cm, cmProjects, tls };
  };

  const branches = useMemo(() => {
    if (isCM) {
      const me = (users || []).find((u) => u.id === user.id);
      return me ? [buildCMBranch(me)] : [];
    }
    if (isAdmin || isDA) return allCMs.map(buildCMBranch);
    return [];
    // eslint-disable-next-line
  }, [isCM, isAdmin, isDA, allCMs, contentProjects, users, user?.id]);

  if (branches.length === 0) return null;

  const totals = branches.reduce(
    (acc, b) => {
      acc.cms += 1;
      acc.tls += b.tls.length;
      acc.projects += b.cmProjects.length;
      return acc;
    },
    { cms: 0, tls: 0, projects: 0 },
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="team-hierarchy-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition text-left"
        data-testid="team-hierarchy-toggle"
      >
        <div className="h-9 w-9 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center shrink-0">
          <Network className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800">Team Hierarchy Overview</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {isCM
              ? `Your team — ${totals.tls} Team Lead${totals.tls === 1 ? "" : "s"} · ${totals.projects} project${totals.projects === 1 ? "" : "s"}`
              : `${totals.cms} Content Manager${totals.cms === 1 ? "" : "s"} · ${totals.tls} Team Lead${totals.tls === 1 ? "" : "s"} · ${totals.projects} project${totals.projects === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="text-slate-400 shrink-0">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-100" data-testid="team-hierarchy-body">
          {branches.map(({ cm, cmProjects, tls }) => {
            const isCMOpen = expandedCMs.has(cm.id);
            return (
              <div key={cm.id} className="bg-white">
                <button
                  type="button"
                  onClick={() => toggleCM(cm.id)}
                  className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-purple-50/40 text-left"
                  data-testid={`hierarchy-cm-${cm.id}`}
                >
                  <span className="text-slate-400 shrink-0">
                    {isCMOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-purple-600 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5 shrink-0">CM</span>
                  <span className="text-sm font-medium text-slate-800 truncate flex-1">{cm.name}</span>
                  <span className="text-[11px] text-slate-500 inline-flex items-center gap-1 shrink-0">
                    <Users className="h-3 w-3" /> {tls.length} TL
                  </span>
                  <span className="text-[11px] text-slate-500 inline-flex items-center gap-1 shrink-0">
                    <FolderKanban className="h-3 w-3" /> {cmProjects.length} project{cmProjects.length === 1 ? "" : "s"}
                  </span>
                </button>

                {isCMOpen && (
                  <div className="bg-slate-50/60 border-t border-slate-100">
                    {tls.length === 0 && (
                      <div className="px-14 py-3 text-xs italic text-slate-400" data-testid={`hierarchy-cm-empty-${cm.id}`}>
                        No Team Leads under this Content Manager yet.
                      </div>
                    )}
                    {tls.map((tl) => {
                      const isTLOpen = expandedTLs.has(tl.id);
                      const tlProjects = cmProjects.filter((p) => (p.team_lead_ids || []).includes(tl.id));
                      return (
                        <div key={tl.id}>
                          <button
                            type="button"
                            onClick={() => toggleTL(tl.id)}
                            className="w-full flex items-center gap-3 pl-14 pr-5 py-2 hover:bg-white text-left"
                            data-testid={`hierarchy-tl-${tl.id}`}
                          >
                            <span className="text-slate-400 shrink-0">
                              {isTLOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                            </span>
                            <span className={`text-[10px] font-mono uppercase tracking-wider rounded px-1.5 py-0.5 shrink-0 ${tl.tl_kind === "publishing" ? "text-amber-700 bg-amber-50 border border-amber-200" : "text-sky-700 bg-sky-50 border border-sky-200"}`}>
                              {tl.tl_kind === "publishing" ? "Pub TL" : "TL"}
                            </span>
                            <span className="text-sm text-slate-700 truncate flex-1">{tl.name}</span>
                            <span className="text-[11px] text-slate-500 inline-flex items-center gap-1 shrink-0">
                              <FolderKanban className="h-3 w-3" /> {tlProjects.length} project{tlProjects.length === 1 ? "" : "s"}
                            </span>
                          </button>
                          {isTLOpen && (
                            <div className="pl-20 pr-5 pb-2 space-y-0.5 bg-white/80">
                              {tlProjects.length === 0 ? (
                                <div className="text-xs italic text-slate-400 py-1" data-testid={`hierarchy-tl-empty-${tl.id}`}>
                                  No projects assigned to this Team Lead.
                                </div>
                              ) : (
                                tlProjects.map((p) => (
                                  <Link
                                    key={p.id}
                                    to={`/projects/${p.id}`}
                                    className="block text-xs text-slate-600 hover:text-purple-700 hover:underline truncate py-0.5"
                                    data-testid={`hierarchy-project-${tl.id}-${p.id}`}
                                  >
                                    · {p.name}
                                  </Link>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
